import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { useToast } from '../../hooks/use-toast';
import { 
  Plus, 
  Trash2, 
  Car,
  MapPin,
  DollarSign,
  Calendar,
  Navigation,
  Loader2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const MileageTab = ({ businessId, token, authHeaders, selectedYear }) => {
  const { toast } = useToast();
  const [mileageLogs, setMileageLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    start_location: '',
    end_location: '',
    miles: '',
    purpose: '',
    round_trip: false,
    notes: ''
  });

  const fetchMileage = async () => {
    if (!businessId) return;
    try {
      const response = await axios.get(
        `${API}/api/mybookie/mileage/${businessId}?year=${selectedYear}`, 
        authHeaders
      );
      setMileageLogs(response.data.mileage_logs);
      setSummary(response.data.summary);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMileage();
  }, [businessId, selectedYear]);

  const handleCreate = async () => {
    if (!formData.start_location || !formData.end_location || !formData.miles || !formData.purpose) {
      toast({ title: "Error", description: "Please fill in all required fields", variant: "destructive" });
      return;
    }

    setCreating(true);
    try {
      await axios.post(`${API}/api/mybookie/mileage`, {
        business_id: businessId,
        date: formData.date,
        start_location: formData.start_location,
        end_location: formData.end_location,
        miles: parseFloat(formData.miles),
        purpose: formData.purpose,
        round_trip: formData.round_trip,
        notes: formData.notes
      }, authHeaders);
      
      toast({ title: "Success", description: "Mileage logged" });
      setShowCreate(false);
      setFormData({
        date: new Date().toISOString().split('T')[0],
        start_location: '',
        end_location: '',
        miles: '',
        purpose: '',
        round_trip: false,
        notes: ''
      });
      fetchMileage();
    } catch (error) {
      toast({ title: "Error", description: "Failed to log mileage", variant: "destructive" });
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (mileageId) => {
    if (!window.confirm('Delete this mileage log?')) return;
    try {
      await axios.delete(`${API}/api/mybookie/mileage/${mileageId}`, authHeaders);
      toast({ title: "Success", description: "Mileage log deleted" });
      fetchMileage();
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete", variant: "destructive" });
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const purposeOptions = [
    'Client Meeting',
    'Supplies Run',
    'Bank/Post Office',
    'Property Visit',
    'Business Travel',
    'Equipment Pickup',
    'Conference/Event',
    'Other'
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-[#0A2B1D]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-[#E6E8E6]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#E2ECE7] rounded-sm">
                  <Car className="w-5 h-5 text-[#0A2B1D]" />
                </div>
                <div>
                  <p className="text-xs text-[#5C6661]">Total Miles</p>
                  <p className="text-lg font-semibold text-[#111614]">
                    {summary.total_miles.toLocaleString()}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-[#E6E8E6]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#E2ECE7] rounded-sm">
                  <DollarSign className="w-5 h-5 text-[#0A2B1D]" />
                </div>
                <div>
                  <p className="text-xs text-[#5C6661]">Tax Deduction</p>
                  <p className="text-lg font-semibold text-[#3A6E53]">
                    {formatCurrency(summary.total_deduction)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-[#E6E8E6]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#E2ECE7] rounded-sm">
                  <Navigation className="w-5 h-5 text-[#0A2B1D]" />
                </div>
                <div>
                  <p className="text-xs text-[#5C6661]">Total Trips</p>
                  <p className="text-lg font-semibold text-[#111614]">
                    {summary.total_trips}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-[#E6E8E6]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-50 rounded-sm">
                  <Calendar className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-[#5C6661]">IRS Rate</p>
                  <p className="text-lg font-semibold text-[#111614]">
                    ${summary.rate_per_mile}/mi
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 p-4 rounded-sm">
        <p className="text-sm text-blue-800">
          <strong>IRS Standard Mileage Rate (2026):</strong> $0.70 per mile for business use. 
          Keep records of date, destination, business purpose, and miles driven.
        </p>
      </div>

      {/* Header with Create Button */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-[#111614]">Mileage Logs</h3>
        <Button 
          onClick={() => setShowCreate(true)}
          className="bg-[#0A2B1D] hover:bg-[#254D3D] rounded-sm"
        >
          <Plus className="w-4 h-4 mr-2" /> Log Trip
        </Button>
      </div>

      {/* Mileage List */}
      <Card className="border-[#E6E8E6]">
        <CardContent className="p-0">
          {mileageLogs.length === 0 ? (
            <div className="p-8 text-center text-[#5C6661]">
              <Car className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>No mileage logged yet. Start tracking your business trips!</p>
            </div>
          ) : (
            <div className="divide-y divide-[#E6E8E6]">
              {mileageLogs.map((log) => (
                <div key={log.id} className="p-4 hover:bg-[#F6F5F2] transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <MapPin className="w-4 h-4 text-[#5C6661]" />
                        <span className="font-medium text-[#111614]">
                          {log.start_location} → {log.end_location}
                        </span>
                        {log.round_trip && (
                          <span className="text-xs bg-[#E2ECE7] text-[#0A2B1D] px-2 py-0.5 rounded-sm">
                            Round Trip
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-[#5C6661] mb-1">{log.purpose}</p>
                      <p className="text-xs text-[#8A948F]">{log.date}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-[#111614]">{log.miles} mi</p>
                      <p className="text-sm text-[#3A6E53]">{formatCurrency(log.deduction)}</p>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleDelete(log.id)}
                        className="text-red-600 hover:text-red-700 mt-1"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="text-[#111614]">Log Business Trip</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-[#111614]">Date *</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="bg-[#FAFAF8] border-[#E6E8E6]"
              />
            </div>
            
            <div>
              <Label className="text-[#111614]">From (Start Location) *</Label>
              <Input
                value={formData.start_location}
                onChange={(e) => setFormData({ ...formData, start_location: e.target.value })}
                placeholder="e.g., Home Office, 123 Main St"
                className="bg-[#FAFAF8] border-[#E6E8E6]"
              />
            </div>
            
            <div>
              <Label className="text-[#111614]">To (End Location) *</Label>
              <Input
                value={formData.end_location}
                onChange={(e) => setFormData({ ...formData, end_location: e.target.value })}
                placeholder="e.g., Client Office, Bank"
                className="bg-[#FAFAF8] border-[#E6E8E6]"
              />
            </div>
            
            <div>
              <Label className="text-[#111614]">Miles (one way) *</Label>
              <Input
                type="number"
                step="0.1"
                value={formData.miles}
                onChange={(e) => setFormData({ ...formData, miles: e.target.value })}
                placeholder="0.0"
                className="bg-[#FAFAF8] border-[#E6E8E6]"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <Checkbox
                id="round_trip"
                checked={formData.round_trip}
                onCheckedChange={(checked) => setFormData({ ...formData, round_trip: checked })}
              />
              <Label htmlFor="round_trip" className="text-sm text-[#5C6661]">
                Round trip (doubles miles automatically)
              </Label>
            </div>
            
            <div>
              <Label className="text-[#111614]">Purpose *</Label>
              <select
                value={formData.purpose}
                onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
                className="w-full p-2 bg-[#FAFAF8] border border-[#E6E8E6] rounded-sm text-sm"
              >
                <option value="">Select purpose...</option>
                {purposeOptions.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
            
            <div>
              <Label className="text-[#111614]">Notes (optional)</Label>
              <Input
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional details"
                className="bg-[#FAFAF8] border-[#E6E8E6]"
              />
            </div>
            
            {formData.miles && (
              <div className="bg-[#E2ECE7] p-3 rounded-sm">
                <p className="text-sm text-[#0A2B1D]">
                  <strong>Estimated Deduction:</strong>{' '}
                  {formatCurrency((parseFloat(formData.miles) || 0) * (formData.round_trip ? 2 : 1) * 0.70)}
                </p>
                <p className="text-xs text-[#5C6661] mt-1">
                  {(parseFloat(formData.miles) || 0) * (formData.round_trip ? 2 : 1)} miles × $0.70/mile
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)} className="border-[#E6E8E6]">
              Cancel
            </Button>
            <Button 
              onClick={handleCreate}
              disabled={creating}
              className="bg-[#0A2B1D] hover:bg-[#254D3D] rounded-sm"
            >
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Log Trip'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MileageTab;
